import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Database } from "lucide-react";

const DBS = ["aiservice", "eSource", "regez"];

// Super admins switch an agent's MongoDB tools on and pick its databases; everyone else sees the setting.
export default function AgentMongoTools({ agent, onSaved }) {
    const { user } = useAuth();
    const isSuper = user?.role === "super_admin";
    const cur = agent.mongo_tools || { enabled: false, databases: [] };
    const [enabled, setEnabled] = useState(!!cur.enabled);
    const [dbs, setDbs] = useState(cur.databases?.length ? cur.databases : DBS);
    const [configured, setConfigured] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { api.get("/admin/mongo-mcp/status").then((r) => setConfigured(!!r.data.configured)).catch(() => setConfigured(false)); }, []);
    const dirty = enabled !== !!cur.enabled || (enabled && JSON.stringify([...dbs].sort()) !== JSON.stringify([...(cur.databases || [])].sort()));
    const toggleDb = (d) => setDbs(dbs.includes(d) ? dbs.filter((x) => x !== d) : [...dbs, d]);
    const save = async () => {
        setBusy(true);
        try {
            const { data } = await api.put(`/admin/agents/${agent.id}/mongo-tools`, { enabled, databases: enabled ? dbs : cur.databases || [] });
            toast.success(`MongoDB tools ${data.mongo_tools.enabled ? "on" : "off"}`);
            onSaved(data.mongo_tools);
        } catch (e) { toast.error(formatApiErrorDetail(e)); }
        finally { setBusy(false); }
    };
    return (
        <div className="border border-white/10 bg-[#0a0a0a] rounded-md p-4 mt-5" data-testid="agent-mongo-tools">
            <div className="flex items-center gap-2 mb-3">
                <Database className="w-3.5 h-3.5 text-[#A78BFA]" />
                <div className="text-sm font-medium flex-1">MongoDB tools (live SiteCentric data)</div>
                <label className={`flex items-center gap-2 text-xs ${isSuper ? "cursor-pointer" : "opacity-60"}`}>
                    <input type="checkbox" checked={enabled} disabled={!isSuper} onChange={(e) => setEnabled(e.target.checked)} data-testid="agent-mongo-enabled" />
                    {enabled ? "On" : "Off"}
                </label>
            </div>
            <div className="flex flex-wrap gap-2 mb-3" data-testid="agent-mongo-databases">
                {DBS.map((d) => (
                    <label key={d} className={`flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1 rounded border border-white/10 ${!enabled || !isSuper ? "opacity-40" : "cursor-pointer hover:bg-white/5"}`}>
                        <input type="checkbox" checked={enabled && dbs.includes(d)} disabled={!enabled || !isSuper} onChange={() => toggleDb(d)} data-testid={`agent-mongo-db-${d}`} /> {d}
                    </label>
                ))}
            </div>
            <div className="text-[11px] text-white/45 leading-relaxed" data-testid="agent-mongo-note">
                {configured === false ? "MongoDB MCP is not configured in this environment — the tools stay hidden from this agent. "
                    : "The model reads with mongo_* tools next to its Data Lake tools. Tenant runs are locked to the caller's company; super admin runs read all companies. "}
                {!isSuper && "Only super admins can change this."}
            </div>
            {isSuper && (
                <div className="flex justify-end mt-3">
                    <button onClick={save} disabled={!dirty || busy || (enabled && !dbs.length)} data-testid="agent-mongo-save"
                        className="px-3 py-1.5 rounded-md text-xs font-medium bg-[#7C3AED] text-white hover:bg-[#6D28D9] disabled:bg-white/5 disabled:text-white/30 transition-colors">
                        {busy ? "Saving…" : "Save MongoDB tools"}
                    </button>
                </div>
            )}
        </div>
    );
}
